// Copyright (c) 2022 WiseTime. All rights reserved.

import React, { useMemo } from "react"
import { ListItem } from "features/shared/search-dropdown/styles"
import styled, { css } from "styled-components/macro"
import { darken, transparentize } from "polished"
import TagLabel from "./TagLabel"
import ActivityTypeLabel from "./ActivityTypeLabel"
import { t } from "@lingui/macro"
import { selectTagColor } from "../slices/timeline"
import { useSelector } from "react-redux"
import { RootState } from "../../../app/store"
import { TimeGroupListItem } from "../timeGroups"
import TagCategoryLabel from "./TagCategoryLabel"

const StyledListItem = styled(props => <ListItem {...props} />)`
  && {
    min-height: 26px;
    justify-content: center;
    align-items: flex-start;
    flex-direction: column;
    padding-left: 10px;
    padding-right: 10px;
    border-left: 10px solid ${({ tagColor, isMatch }) =>
    transparentize(isMatch ? 0 : 0.9, darken(0.3, tagColor))};
  }
`

const NarrativeLabel = styled.p`
  line-height: 16px;
  margin: 0;
`

const NoNarrativeText = styled(NarrativeLabel)`
  color: ${({ theme }) => theme.colors.silverSand};
  font-style: italic;
  font-weight: 300;
`

const StyledTagLabel = styled(TagLabel)`
  cursor: pointer;
  margin: 4px 4px 0 0;
`

const StyledActivityTypeLabel = styled(ActivityTypeLabel)`
  margin: 4px 0 0 4px;
  cursor: pointer;
  border-color: ${({ theme }): string => theme.colors.mercury};
  background-color: ${({ theme }): string => theme.colors.mercury};
`

const Header = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 5px;
`

const ListItemsMaxWidth = css`
  @media (max-width: 400px) {
    > * {
      max-width: 120px;
    }
  }
`

const TagsList = styled.div`
  flex: 1;
  align-self: stretch;
  ${ListItemsMaxWidth};
`

const List = styled.div`
  ${ListItemsMaxWidth};
`

type Props = {
  listItem: TimeGroupListItem,
  activeId: string | undefined,
  isMatch: boolean,
  handleAddToGroup: (id: string) => void,
}

const AddToGroupDropdownListItem = ({
  listItem: {
    id,
    narrative,
    tags,
    tagCategories,
    activityTypes,
  },
  activeId,
  isMatch,
  handleAddToGroup,
}: Props): React.ReactElement<Props> => {
  const tagId = tags[0]?.id ?? ""
  const tagCategoryId = tagCategories[0]?.id ?? ""
  const selectColor = useMemo(selectTagColor, [])
  const tagColor = useSelector((state: RootState) => selectColor(state, tagId || tagCategoryId))

  return (
    <StyledListItem
      id={activeId === id ? id : ""}
      isActive={activeId === id}
      tagColor={tagColor}
      isMatch={isMatch}
      onClick={(): void => handleAddToGroup(id)}
    >
      {(tags.length !== 0 || activityTypes.length !== 0 || tagCategories.length !== 0) && (
        <Header>
          <TagsList>
            {tags.map((tagRef) => (
              <StyledTagLabel
                key={tagRef.id}
                tagRef={tagRef}
                withOverlay={false}
                isDisabled={false}
                isDisabledGroup={true}
                stopPropagation={false}
              />
            ))}
            {tagCategories.map(tagCategoryRef => (
              <TagCategoryLabel key={tagCategoryRef.id} tagCategoryRef={tagCategoryRef}/>
            ))}
          </TagsList>
          <List>
            {activityTypes.map(activityTypeRef => (
              <StyledActivityTypeLabel
                key={activityTypeRef.id}
                isDisabled
                isDisabledGroup
                stopPropagation={false}
                activityTypeRef={activityTypeRef}
              />
            ))}
          </List>
        </Header>
      )}
      {narrative !== ""
        ? <NarrativeLabel>{narrative}</NarrativeLabel>
        : <NoNarrativeText>{t({ id: "label_no_narrative_yet", message: "No narrative yet" })}</NoNarrativeText>
      }
    </StyledListItem>
  )
}

export default AddToGroupDropdownListItem
